import type {
  DirectSaleDecorationMethod,
  DirectSalePriceQuote,
} from '@designdeck/shared';
import { getEmbedOrigin } from '@/api/directSale';

/**
 * Everything the buying system needs to turn a finished configuration into an
 * order. DesignDeck runs no cart and takes no payment — see
 * docs/features/FEATURE_SHOP_WIZARD.md — so this is a handover, not a
 * checkout.
 */
export interface CheckoutHandover {
  shop: string | null;
  /**
   * The shop's own correlation id from the iframe URL (?sessionRef=…),
   * echoed back verbatim so the shop can match this handover to its
   * customer or cart without parsing anything else.
   */
  sessionRef: string | null;
  /**
   * Id of the server-persisted quote. The shop MUST redeem this
   * server-to-server (GET /products/direct-sale/quotes/:quoteId, signed
   * like the iframe URL) and charge the redeemed amounts — everything else
   * in this payload, `price` included, is client-assembled and forgeable.
   */
  quoteId: string;
  /**
   * The design committed for this checkout: production prints its frozen
   * specification. The checkout commits before it hands over, so this is
   * always set, and the redeemed quote carries the same id.
   */
  designId: string;
  /**
   * Shop Language the quote was priced in, as the API normalised it
   * (e.g. "pt" for "pt-BR"). Position names in `price` are in it, and the
   * redeemed quote carries the same value.
   */
  language: string;
  productId: string;
  colorId: string | null;
  /** The first item's size — for shops that read one size. */
  sizeId: string | null;
  /**
   * What is bought: one entry per size with its quantity, as the quote
   * priced it. Redeeming the quote returns the same list.
   */
  items: { sizeId: string | null; sizeName: string | null; quantity: number }[];
  /** Sum of the items' quantities. */
  quantity: number;
  /**
   * The production type the quote was priced with (`processingType` is the
   * Phoenix id). Redeeming the quote returns the same method.
   */
  decorationMethod: DirectSaleDecorationMethod | null;
  positions: {
    positionId: string;
    widthMm: number | null;
    heightMm: number | null;
    /** Rendered preview of what the customer designed on this position. */
    previewDataUrl: string | null;
  }[];
  /** Server-calculated, rendered verbatim — never recomputed client-side. */
  price: DirectSalePriceQuote;
}

/**
 * Hands the finished configuration to the embedding page.
 *
 * The transport is postMessage because the shop owns the iframe and already
 * has a listener boundary there. The shop redeems the payload's quoteId
 * server-to-server for the authoritative price.
 *
 * The target origin is the shop's CONFIGURED embedding origin from the
 * session exchange; document.referrer is only the fallback for shops that
 * have not configured one — referrer alone breaks permanently under
 * no-referrer policies. Never '*': the payload contains the customer's
 * design and the priced configuration, and broadcasting it to any
 * listening frame would leak both.
 */
export function submitCheckout(
  handover: CheckoutHandover,
  targetOrigin: string = getEmbedOrigin() ??
    (document.referrer ? new URL(document.referrer).origin : '')
): boolean {
  if (typeof window === 'undefined') return false;

  // Not embedded, or the embedding origin is unknown: refuse rather than
  // fall back to a wildcard target.
  if (window.parent === window || !targetOrigin) return false;

  window.parent.postMessage(
    { type: 'designdeck:direct-sale:checkout', payload: handover },
    targetOrigin
  );

  return true;
}
